import { stringEnum } from 'app/config/utils';
import { HELLO_CONTINUATION } from 'app/models/localStorage/constants';
import { Action, LoginIntentType } from 'app/services/oauth/constants';
import createErrorType from 'app/utils/createErrorType';
import { getDangerousNativeLocalStorage } from 'app/utils/env';
import { decodeOrThrow } from 'app/utils/ioTs';
import { IknowActionType } from 'app/services/iknow/constants';
import * as t from 'io-ts';
import { AuthProviderCodec } from 'app/constants/enums/AuthProvider';
import { IdentityGuidCodec } from 'app/types/guid';
import { IdentityEntityStringIdCodec } from 'app/types/entity/codecs';

export class ContinuationMissingError extends createErrorType('ContinuationMissingError') {
}

const DoOauth = t.type({
  returnUriReference: t.string,
  provider: AuthProviderCodec,
  extraParams: t.record(t.string, t.unknown),
  isReauthentication: t.boolean,
  intent: LoginIntentType,
  brandId: IdentityEntityStringIdCodec,
});

const ContinuationBaseCodec = t.type({
  action: stringEnum<Action>(Action, 'Action'),
  state: IdentityGuidCodec,
});

const ContinuationAuthCodec = t.intersection([
  ContinuationBaseCodec,
  t.type({
    action: t.literal(Action.AUTH),
    isReauthentication: t.boolean,
    redirectUriReference: t.string,
    provider: AuthProviderCodec,
  }),
]);

const ContinuationAttachCodec = t.intersection([
  ContinuationBaseCodec,
  t.type({
    action: t.literal(Action.ATTACH),
    redirectUriReference: t.string,
    provider: AuthProviderCodec,
  }),
]);

const ContinuationDmmLoginCodec = t.intersection([
  ContinuationBaseCodec,
  t.type({
    action: t.literal(Action.DMM_LOGIN),
    next: DoOauth,
  }),
]);

const ContinuationIknowLoginCodec = t.intersection([
  ContinuationBaseCodec,
  t.type({
    action: t.literal(Action.IKNOW_LOGIN),
    redirectUriReference: t.string,
    pendingAction: stringEnum<IknowActionType>(IknowActionType, 'IknowActionType'),
    actionTargetId: IdentityEntityStringIdCodec,
  }),
]);

export const ContinuationCodec = t.union([
  ContinuationAuthCodec,
  ContinuationAttachCodec,
  ContinuationDmmLoginCodec,
  ContinuationIknowLoginCodec,
]);

interface IResultTypes {
  [Action.AUTH]: t.TypeOf<typeof ContinuationAuthCodec>;
  [Action.ATTACH]: t.TypeOf<typeof ContinuationAttachCodec>;
  [Action.DMM_LOGIN]: t.TypeOf<typeof ContinuationDmmLoginCodec>;
  [Action.IKNOW_LOGIN]: t.TypeOf<typeof ContinuationIknowLoginCodec>;
}

export type Continuation = t.TypeOf<typeof ContinuationCodec>;

export const saveContinuation = (c: Continuation) => {
  const localStorage = getDangerousNativeLocalStorage();
  localStorage.setItem(HELLO_CONTINUATION, JSON.stringify(c));
};

export const loadContinuation = <T extends Action>(
  expectedAction: T, expectedState: string,
): IResultTypes[T] => {
  const localStorage = getDangerousNativeLocalStorage();

  const continuationStr = localStorage.getItem(HELLO_CONTINUATION);
  if (!continuationStr) {
    throw new ContinuationMissingError('No continuation found');
  }

  const continuation = decodeOrThrow(ContinuationCodec, JSON.parse(continuationStr), (errors) => {
    throw new ContinuationMissingError(errors.join('\n'));
  });

  if (continuation.state !== expectedState) {
    throw new ContinuationMissingError(
      `Expected state: ${expectedState}, but was ${continuation.state}`);
  }

  if (isExpectedAction(continuation, expectedAction)) {
    // Continuation at this point has been consumed.
    localStorage.removeItem(HELLO_CONTINUATION);

    return continuation;
  } else {
    throw new ContinuationMissingError(
      `Expected action: ${expectedAction}, but was ${continuation.action}`);
  }
};

function isExpectedAction<T extends Action>(
  continuation: Continuation,
  expectedAction: T,
): continuation is IResultTypes[T] {
  return continuation.action === expectedAction;
}
